import React, { useState, useEffect } from 'react';
import { db } from '../firebase/config';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { CartoonAvatar, XPBadge, RankMedal, LevelPill, TrophyIcon } from '../components/GamificationAssets';

export default function Leaderboard() {
    const [students, setStudents] = useState([]);
    const [lbCategory, setLbCategory] = useState('TOTAL_XP');
    const [lbLevel, setLbLevel] = useState('ALL');

    useEffect(() => {
        const q = query(collection(db, "users"), where("role", "==", "STUDENT"));
        const unsub = onSnapshot(q, (snap) => {
            const list = [];
            snap.forEach(d => {
                const data = d.data();
                list.push({
                    id: d.id, name: data.name || 'Unknown Ninja', avatar: data.avatar || '👤',
                    level: data.level || 1,
                    totalXp: data.totalXp || 0, hwXp: data.hwXp || 0, tournamentXp: data.tournamentXp || 0
                });
            });
            setStudents(list);
        });
        return () => unsub();
    }, []);

    let filteredStudents = [...students];
    if (lbLevel !== 'ALL') filteredStudents = filteredStudents.filter(s => s.level.toString() === lbLevel);

    filteredStudents.sort((a, b) => {
        if (lbCategory === 'HOMEWORK') return b.hwXp - a.hwXp;
        if (lbCategory === 'TOURNAMENT') return b.tournamentXp - a.tournamentXp;
        return b.totalXp - a.totalXp;
    });

    const getScore = (student) => {
        if (lbCategory === 'HOMEWORK') return student.hwXp;
        if (lbCategory === 'TOURNAMENT') return student.tournamentXp;
        return student.totalXp;
    };

    const top3 = filteredStudents.slice(0, 3);
    const rest = filteredStudents.slice(3);

    const podiumOrder = top3.length === 3 ? [top3[1], top3[0], top3[2]] :
                        top3.length === 2 ? [top3[1], top3[0]] :
                        top3.length === 1 ? [top3[0]] : [];

    const podiumHeights = [140, 180, 110]; // silver, gold, bronze
    const podiumColors = [
        { bar: 'linear-gradient(180deg, #C0C0C0, #9CA3AF)', glow: 'rgba(192,192,192,0.3)', text: '#6B7280', rank: 2 },
        { bar: 'linear-gradient(180deg, #FFD700, #F59E0B)', glow: 'rgba(255,215,0,0.35)', text: '#92400E', rank: 1 },
        { bar: 'linear-gradient(180deg, #CD7F32, #B45309)', glow: 'rgba(205,127,50,0.3)', text: '#78350F', rank: 3 },
    ];

    const CATEGORIES = [
        { id: 'TOTAL_XP', label: '🔥 Total XP', color: '#f59e0b' },
        { id: 'HOMEWORK', label: '📚 Homework', color: '#3b82f6' },
        { id: 'TOURNAMENT', label: '⚔️ Arena', color: '#ef4444' },
    ];
    const LEVELS = ['ALL','1','2','3','4','5','6'];
    const levelNames = { ALL: '🌍 All Levels', '1': 'Pawn (Lv.1)', '2': 'Knight (Lv.2)', '3': 'Bishop (Lv.3)', '4': 'Rook (Lv.4)', '5': 'Queen (Lv.5)', '6': 'King (Lv.6)' };

    return (
        <div style={{ width: '100%', maxWidth: '860px', margin: '0 auto', paddingBottom: '60px' }}>
            <style>{`
                @keyframes lb-rise {
                    0%  { transform: translateY(30px); opacity: 0; }
                    100%{ transform: translateY(0);    opacity: 1; }
                }
                @keyframes podium-glow {
                    0%,100% { box-shadow: 0 0 18px var(--g, rgba(255,215,0,0.25)); }
                    50%     { box-shadow: 0 0 35px var(--g, rgba(255,215,0,0.5));  }
                }
                .lb-row-item {
                    display: flex; align-items: center; gap: 14px;
                    padding: 14px 20px; border-radius: 18px;
                    background: var(--bg-primary);
                    border: 1.5px solid rgba(255,255,255,0.7);
                    box-shadow: 6px 6px 14px rgba(163,177,198,0.2), -6px -6px 14px rgba(255,255,255,0.5);
                    transition: transform 0.25s cubic-bezier(0.34,1.56,0.64,1), box-shadow 0.25s;
                    animation: lb-rise 0.4s ease both;
                }
                .lb-row-item:hover {
                    transform: translateY(-3px) scale(1.01);
                    box-shadow: 8px 8px 22px rgba(163,177,198,0.32), -8px -8px 22px rgba(255,255,255,0.75);
                }
                .category-pill {
                    padding: 8px 18px; border-radius: 30px; font-size: 13px; font-weight: 800;
                    cursor: pointer; border: 2px solid transparent;
                    transition: all 0.2s cubic-bezier(0.34,1.56,0.64,1);
                    font-family: 'Plus Jakarta Sans', sans-serif;
                }
                .category-pill.active { transform: scale(1.06); }
                .level-chip {
                    padding: 6px 14px; border-radius: 24px; font-size: 12px; font-weight: 700;
                    cursor: pointer; border: 1.5px solid rgba(163,177,198,0.4);
                    background: var(--bg-primary);
                    box-shadow: 4px 4px 10px rgba(163,177,198,0.18), -4px -4px 10px rgba(255,255,255,0.5);
                    transition: all 0.2s;
                    font-family: 'Plus Jakarta Sans', sans-serif;
                }
                .level-chip.active {
                    background: linear-gradient(135deg, #2563eb, #1e3a8a);
                    color: #fff !important; border-color: transparent;
                    box-shadow: 0 4px 14px rgba(37,99,235,0.35);
                    transform: scale(1.06);
                }
            `}</style>

            {/* ── HEADER ─────────────────────────────────────────────────────── */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '30px' }}>
                <TrophyIcon size={50} color="#f59e0b" />
                <div>
                    <div style={{ fontSize: '11px', color: '#f59e0b', fontWeight: 900, textTransform: 'uppercase', letterSpacing: '1.5px' }}>
                        Hall of Champions
                    </div>
                    <h1 style={{ margin: 0, fontSize: '28px', fontWeight: 900, color: 'var(--text-primary)', lineHeight: 1.1 }}>
                        Academy Leaderboard
                    </h1>
                </div>
            </div>

            {/* ── CATEGORY FILTER ─────────────────────────────────────────────── */}
            <div style={{
                display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '20px',
                padding: '16px 20px', borderRadius: '20px',
                background: 'var(--bg-primary)',
                boxShadow: '6px 6px 16px rgba(163,177,198,0.22), -6px -6px 16px rgba(255,255,255,0.55)',
                border: '1.5px solid rgba(255,255,255,0.7)',
            }}>
                {CATEGORIES.map(cat => (
                    <button
                        key={cat.id}
                        onClick={() => setLbCategory(cat.id)}
                        className={`category-pill${lbCategory === cat.id ? ' active' : ''}`}
                        style={{
                            background: lbCategory === cat.id ? cat.color : 'transparent',
                            color: lbCategory === cat.id ? '#fff' : 'var(--text-secondary)',
                            borderColor: lbCategory === cat.id ? cat.color : 'rgba(163,177,198,0.35)',
                            boxShadow: lbCategory === cat.id ? `0 4px 14px ${cat.color}55` : 'none',
                        }}
                    >{cat.label}</button>
                ))}
                <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    {LEVELS.map(lv => (
                        <button
                            key={lv}
                            onClick={() => setLbLevel(lv)}
                            className={`level-chip${lbLevel === lv ? ' active' : ''}`}
                            style={{ color: lbLevel === lv ? '#fff' : 'var(--text-secondary)' }}
                        >{lv === 'ALL' ? '🌍 All' : `Lv.${lv}`}</button>
                    ))}
                </div>
            </div>

            {/* ── PODIUM ──────────────────────────────────────────────────────── */}
            {podiumOrder.length > 0 && (
                <div style={{
                    padding: '30px 20px 0 20px', marginBottom: '30px',
                    background: 'linear-gradient(180deg, #1e3a8a 0%, #1e40af 60%, var(--bg-primary) 100%)',
                    borderRadius: '28px', overflow: 'hidden',
                    border: '2px solid rgba(255,255,255,0.5)',
                    boxShadow: '0 20px 50px rgba(37,99,235,0.2)',
                }}>
                    {/* Podium title */}
                    <div style={{ textAlign: 'center', color: '#fff', fontSize: '13px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '2px', marginBottom: '28px', opacity: 0.8 }}>
                        ✨ Top Champions ✨
                    </div>

                    {/* Podium columns */}
                    <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'center', gap: '8px' }}>
                        {podiumOrder.map((student, i) => {
                            const cfg = podiumColors[i];
                            const isGold = cfg.rank === 1;
                            const h = podiumHeights[i];
                            return (
                                <div key={student.id} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1, maxWidth: '200px' }}>
                                    {/* Avatar + Name float above bar */}
                                    <CartoonAvatar
                                        name={student.name}
                                        size={isGold ? 72 : 58}
                                        avatarUrl={student.avatar}
                                        rank={cfg.rank}
                                        style={{ marginBottom: '10px', animation: isGold ? 'float-cute 3s ease-in-out infinite' : 'none' }}
                                    />
                                    <div style={{ color: '#fff', fontWeight: 900, fontSize: isGold ? '15px' : '13px', textAlign: 'center', marginBottom: '4px', maxWidth: '120px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {student.name}
                                    </div>
                                    <div style={{ marginBottom: '10px' }}>
                                        <XPBadge xp={getScore(student)} size={isGold ? 'md' : 'sm'} color={isGold ? 'gold' : 'blue'} />
                                    </div>

                                    {/* Podium bar */}
                                    <div style={{
                                        width: '100%', height: `${h}px`,
                                        background: cfg.bar,
                                        borderRadius: '12px 12px 0 0',
                                        display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
                                        paddingTop: '14px',
                                        boxShadow: isGold ? `0 -6px 25px rgba(255,215,0,0.5), inset 0 2px 8px rgba(255,255,255,0.25)` : `inset 0 2px 8px rgba(255,255,255,0.2)`,
                                        position: 'relative',
                                    }}>
                                        <RankMedal rank={cfg.rank} size={isGold ? 46 : 34} />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* ── RANKED LIST (4th onwards) ────────────────────────────────── */}
            {filteredStudents.length === 0 ? (
                <div style={{ textAlign: 'center', color: 'var(--text-secondary)', padding: '60px 20px', fontSize: '15px', fontWeight: 700 }}>
                    🏄 No students found yet — be the first!
                </div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {(rest.length > 0 && top3.length > 0) && (
                        <div style={{ fontSize: '11px', fontWeight: 800, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '1.5px', padding: '4px 8px', marginBottom: '4px' }}>
                            Remaining Rankings
                        </div>
                    )}
                    {(top3.length === 0 ? filteredStudents : rest).map((s, index) => {
                        const rank = (top3.length > 0 ? 3 : 0) + index + 1;
                        return (
                            <div key={s.id} className="lb-row-item" style={{ animationDelay: `${index * 0.04}s` }}>
                                {/* Rank number */}
                                <div style={{
                                    width: '34px', height: '34px', borderRadius: '10px', flexShrink: 0,
                                    background: 'linear-gradient(135deg, #e2e8f0, #f1f5f9)',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    fontWeight: 900, fontSize: '14px', color: '#475569',
                                    boxShadow: 'inset 2px 2px 5px rgba(255,255,255,0.6), inset -2px -2px 5px rgba(163,177,198,0.25)',
                                }}>
                                    {rank}
                                </div>

                                {/* Avatar */}
                                <CartoonAvatar name={s.name} size={44} avatarUrl={s.avatar} />

                                {/* Info */}
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ fontWeight: 800, fontSize: '15px', color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {s.name}
                                    </div>
                                    <LevelPill level={s.level} />
                                </div>

                                {/* XP */}
                                <XPBadge xp={getScore(s)} size="md" color="green" />
                            </div>
                        );
                    })}
                </div>
            )}

            {filteredStudents.length > 0 && (
                <div style={{ textAlign: 'center', marginTop: '28px', color: 'var(--text-secondary)', fontSize: '12px', fontWeight: 700 }}>
                    🏅 {filteredStudents.length} Champions Ranked
                </div>
            )}
        </div>
    );
}
